import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatApiError } from "@/lib/errors";
import { ArrowLeft, Edit3 } from "lucide-react";

const TABS = ["Overview", "Services", "Payments", "Follow-ups", "Notes", "Activity"];

export default function ClientDetail() {
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [tab, setTab] = useState("Overview");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    api.get(`/crm/clients/${id}`).then((r) => { setClient(r.data); setForm(r.data); });
    api.get(`/crm/activity`, { params: { entity: "client", entity_id: id } }).then((r) => setActivity(r.data.items || []));
  }, [id]);

  const save = async () => {
    try {
      const { data } = await api.patch(`/crm/clients/${id}`, form);
      setClient(data); setEditing(false); toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e, "Failed")); }
  };

  if (!client) return <div className="p-6 text-[#747680]">Loading…</div>;

  return (
    <div className="p-6" data-testid="client-detail">
      <Link to="/app/crm/clients" className="inline-flex items-center gap-1 text-sm text-[#4A4D57] hover:text-[#0B0D17] mb-3"><ArrowLeft className="w-4 h-4" /> Back to clients</Link>
      <div className="rounded-2xl border border-[#E4E5E9] bg-white p-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display font-black text-3xl text-[#0B0D17]">{client.name}</h1>
          <div className="mt-1 text-sm text-[#4A4D57]">{client.mobile || "—"} · {client.email || "—"} · {client.location || "—"}</div>
          <div className="mt-2 flex gap-2 flex-wrap">
            {(client.tags || []).map((t) => <span key={t} className="text-xs px-2 py-0.5 rounded-full bg-[#F5F5F7] text-[#4A4D57]">{t}</span>)}
            <span className="text-xs px-2 py-0.5 rounded-full bg-[#10B981]/10 text-[#10B981] font-semibold">{client.status}</span>
          </div>
        </div>
        <button onClick={() => setEditing(true)} className="vp-btn-ghost inline-flex items-center gap-1 text-sm"><Edit3 className="w-4 h-4" /> Edit</button>
      </div>

      <div className="mt-5 flex items-center gap-1 border-b border-[#E4E5E9]">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} className={"px-4 py-2 text-sm font-semibold border-b-2 " + (tab === t ? "border-[#0B5CFF] text-[#0B5CFF]" : "border-transparent text-[#4A4D57]")} data-testid={`tab-${t}`}>{t}</button>
        ))}
      </div>

      <div className="mt-5">
        {tab === "Overview" && (
          <div className="rounded-xl border border-[#E4E5E9] bg-white p-6 max-w-2xl">
            <h3 className="font-bold text-[#0B0D17] mb-3">Details</h3>
            <div className="grid grid-cols-2 gap-y-3 text-sm">
              {[["Mobile", client.mobile], ["WhatsApp", client.whatsapp], ["Email", client.email], ["Location", client.location], ["Language", client.preferred_language], ["Source", client.lead_source], ["Since", client.client_since && new Date(client.client_since).toLocaleDateString()]].map(([k, v]) => (
                <div key={k}><div className="text-xs uppercase tracking-widest font-bold text-[#747680]">{k}</div><div className="text-[#0B0D17]">{v || "—"}</div></div>
              ))}
            </div>
            {client.notes && <div className="mt-4"><div className="text-xs uppercase tracking-widest font-bold text-[#747680] mb-1">Notes</div><div className="text-sm text-[#0B0D17] whitespace-pre-wrap">{client.notes}</div></div>}
          </div>
        )}
        {tab === "Services" && <EmptyTab title="Services" hint="Assign services to this client (available in Phase 2)." />}
        {tab === "Payments" && <EmptyTab title="Payments" hint="Record payments and generate invoices (Phase 2)." />}
        {tab === "Follow-ups" && <EmptyTab title="Follow-ups" hint="Schedule follow-ups (Phase 3)." />}
        {tab === "Notes" && <EmptyTab title="Notes" hint="Capture private notes about this client (Phase 4)." />}
        {tab === "Activity" && (
          <div className="rounded-xl border border-[#E4E5E9] bg-white p-4">
            {activity.length === 0 && <div className="text-sm text-[#747680] p-4">No activity yet.</div>}
            <ul className="divide-y divide-[#E4E5E9]">
              {activity.map((a) => (
                <li key={a.id} className="py-2 text-sm flex justify-between">
                  <span className="text-[#0B0D17]"><b>{a.user_email || "System"}</b> · {a.action}</span>
                  <span className="text-xs text-[#747680]">{new Date(a.created_at).toLocaleString()}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <h2 className="font-display font-black text-xl mb-4">Edit client</h2>
            <div className="space-y-3">
              {[["name", "Name"], ["mobile", "Mobile"], ["email", "Email"], ["location", "Location"], ["preferred_language", "Language"]].map(([k, ph]) => (
                <input key={k} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder={ph} className="w-full h-10 px-3 rounded-lg border border-[#E4E5E9] text-sm" />
              ))}
              <textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes" rows={3} className="w-full p-3 rounded-lg border border-[#E4E5E9] text-sm" />
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setEditing(false)} className="vp-btn-ghost text-sm">Cancel</button>
              <button onClick={save} className="vp-btn-primary text-sm" data-testid="edit-client-save">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const EmptyTab = ({ title, hint }) => (
  <div className="rounded-xl border border-dashed border-[#E4E5E9] bg-white p-10 text-center">
    <h3 className="font-bold text-[#0B0D17]">{title}</h3>
    <p className="text-sm text-[#747680] mt-1">{hint}</p>
  </div>
);
